<template>
    <section class="dp-layout league-affair-checkin-page">
        <section class="dp-left">
            <DepartmentPane
                directory-scope="teaching"
                pane-title="组织架构"
                @tree-node-click="treeNodeClick"
            />
        </section>

        <section class="dp-right">
            <template v-if="schoolId > 0">
                <div class="filter-bar">
                    <el-form :inline="true" size="small" class="filter-form">
                        <el-form-item label="日期">
                            <el-date-picker
                                v-model="filterForm.dateRange"
                                type="daterange"
                                start-placeholder="开始"
                                end-placeholder="结束"
                                value-format="yyyy-MM-dd"
                                clearable
                                style="width: 240px;"
                                @change="handleSearch"
                            />
                        </el-form-item>
                        <el-form-item label="年月">
                            <el-date-picker
                                v-model="filterForm.yearMonth"
                                type="month"
                                placeholder="全部"
                                value-format="yyyy-MM"
                                clearable
                                style="width: 140px;"
                                @change="handleSearch"
                            />
                        </el-form-item>
                        <el-form-item label="学生">
                            <el-input
                                v-model="filterForm.studentName"
                                placeholder="学生姓名"
                                clearable
                                style="width: 140px;"
                                @keyup.enter.native="handleSearch"
                            />
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
                            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
                            <el-button
                                v-if="hasActionPermission('handleExport')"
                                type="success"
                                icon="el-icon-download"
                                :loading="exporting"
                                @click="handleExport"
                            >导出</el-button>
                        </el-form-item>
                        <el-form-item v-if="scopeHint" class="scope-form-item">
                            <el-tag size="small" type="info" effect="plain" class="scope-tag">
                                <i class="el-icon-location-outline"></i>
                                {{ scopeHint }}
                            </el-tag>
                        </el-form-item>
                    </el-form>
                </div>

                <div class="tbl-scroll-host">
                    <el-table :data="tableData" v-loading="loading" border height="100%" style="width: 100%;" class="compact-table">
                        <el-table-column type="index" label="#" width="50" align="center" />
                        <el-table-column label="刷脸时间" width="160" align="center">
                            <template slot-scope="scope">{{ formatDateTime(scope.row.TouchTime) }}</template>
                        </el-table-column>
                        <el-table-column prop="StudentName" label="学生" min-width="100" show-overflow-tooltip />
                        <el-table-column prop="ClassName" label="班级" min-width="120" show-overflow-tooltip />
                        <el-table-column prop="CampusName" label="校区" min-width="110" show-overflow-tooltip />
                        <el-table-column prop="MeetingTag" label="会议标注" min-width="120" show-overflow-tooltip>
                            <template slot-scope="scope">{{ scope.row.MeetingTag || '-' }}</template>
                        </el-table-column>
                        <el-table-column prop="TerminalName" label="设备" min-width="120" show-overflow-tooltip>
                            <template slot-scope="scope">{{ scope.row.TerminalName || scope.row.TerminalId || '-' }}</template>
                        </el-table-column>
                        <el-table-column label="触发加分" width="90" align="center">
                            <template slot-scope="scope">
                                <el-tag :type="scope.row.ClassScoreApplied ? 'success' : 'info'" size="mini">
                                    {{ scope.row.ClassScoreApplied ? '是' : '否' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="抓拍" width="90" align="center">
                            <template slot-scope="scope">
                                <oss-el-image
                                    v-if="scope.row.KqImageFile"
                                    :src="scope.row.KqImageFile"
                                    :preview-src-list="[scope.row.KqImageFile]"
                                    :school-id="schoolId"
                                    fit="cover"
                                    class="thumb"
                                />
                                <span v-else class="muted">-</span>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <div class="pager">
                    <el-pagination
                        background
                        layout="total, sizes, prev, pager, next"
                        :total="total"
                        :current-page.sync="page"
                        :page-size.sync="pageSize"
                        :page-sizes="[10, 20, 50, 100]"
                        @current-change="loadTableData"
                        @size-change="handleSizeChange"
                    />
                </div>
            </template>

            <div v-else class="no-department-tip">
                <div class="no-department-tip__inner">
                    <i class="el-icon-school"></i>
                    <p>请在左侧组织架构中选择学校 / 校区 / 班级，查看团务考核签到记录</p>
                </div>
            </div>
        </section>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import { getButtonList } from '../../promissionRouter';
import {
    getLeagueAffairCheckInRecords,
    exportLeagueAffairCheckInRecords
} from '../../api/modules/leagueAffair';
import {
    normalizeDeptId,
    treeNodeDeptId,
    deptIdsEqual,
    resolveActiveSchoolId,
} from '../../utils/deptCascaderProps';

export default {
    name: 'LeagueAffairCheckInRecord',
    components: { DepartmentPane },
    data() {
        return {
            schoolId: 0,
            schoolName: '',
            boundSchoolId: 0,
            scopeLabel: '',
            buttonList: [],
            loading: false,
            exporting: false,
            tableData: [],
            page: 1,
            pageSize: 10,
            total: 0,
            filterForm: {
                dateRange: [],
                yearMonth: '',
                campusId: null,
                classId: null,
                studentName: ''
            }
        };
    },
    computed: {
        /** 左侧树选中范围（简短展示，不带「当前范围」前缀） */
        scopeHint() {
            if (this.filterForm.classId) {
                return this.scopeLabel || '指定班级';
            }
            if (this.filterForm.campusId) {
                return this.scopeLabel || '指定校区';
            }
            if (this.schoolId) {
                return this.schoolName || '全校';
            }
            return '';
        }
    },
    created() {
        this.buttonList = getButtonList(this.$route.path) || [];
        const sid = this.resolveSchoolId();
        if (sid) this.applySchoolChange(sid, this.$store.state.activeSchoolName);
    },
    watch: {
        '$store.state.activeSchoolId'(newSchoolId) {
            this.applySchoolChange(newSchoolId, this.$store.state.activeSchoolName);
        }
    },
    methods: {
        hasActionPermission(func) {
            if (!this.buttonList || !this.buttonList.length) return true;
            return this.buttonList.some(button => button.Func === func);
        },

        /** 解析当前学校 Id（字符串，避免雪花 Id 被 Number 截断）。最后修改：2026-10-18 */
        resolveSchoolId() {
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return fromStore;
            const local = normalizeDeptId(this.schoolId);
            if (local) return local;
            return '';
        },

        /** 从树节点向上解析指定组织类型 Id */
        findAncestorDeptId(node, orgType) {
            let cur = node;
            const target = String(orgType || '').toLowerCase();
            while (cur) {
                const data = cur.data || cur;
                const type = String((data.DeptOrgType || data.deptOrgType) || '').toLowerCase();
                if (type === target) {
                    return treeNodeDeptId(data);
                }
                cur = cur.parent;
            }
            return '';
        },

        /**
         * 顶栏切换学校时换校并清空组织范围（不拉列表，等左侧部门树选中后再查）。创建于 2026-09-24
         */
        applySchoolChange(schoolId, schoolName) {
            const id = normalizeDeptId(schoolId);
            if (!id) return;
            const sameSchool = deptIdsEqual(id, this.schoolId) && deptIdsEqual(this.boundSchoolId, id);
            this.schoolId = id;
            this.boundSchoolId = id;
            if (schoolName) this.schoolName = schoolName;
            if (!sameSchool) {
                this.filterForm.campusId = null;
                this.filterForm.classId = null;
                this.scopeLabel = '';
                this.page = 1;
                this.tableData = [];
                this.total = 0;
            }
        },

        /**
         * 左侧组织树选中：学校/校区/年级/班级驱动筛选。创建于 2026-09-24
         */
        treeNodeClick(data, node) {
            if (!data) return;
            const orgType = String(data.DeptOrgType || data.deptOrgType || '').toLowerCase();
            const deptId = treeNodeDeptId(data);
            if (!deptId) return;

            const schoolId = orgType === 'school' || orgType === 'affiliateschool'
                ? deptId
                : (this.findAncestorDeptId(node, 'school')
                    || this.findAncestorDeptId(node, 'affiliateschool')
                    || this.resolveSchoolId()
                    || normalizeDeptId(this.schoolId));
            if (!schoolId) return;

            this.schoolId = schoolId;
            this.boundSchoolId = schoolId;
            if (orgType === 'school' || orgType === 'affiliateschool') {
                this.schoolName = data.label || data.Name || this.schoolName;
            }

            let campusId = null;
            let classId = null;
            if (orgType === 'campus') {
                campusId = deptId;
            } else if (orgType === 'class') {
                classId = deptId;
                campusId = this.findAncestorDeptId(node, 'campus') || null;
            } else if (orgType === 'grade' || orgType === 'period' || orgType === 'stage') {
                campusId = this.findAncestorDeptId(node, 'campus') || null;
            }

            this.filterForm.campusId = campusId;
            this.filterForm.classId = classId;
            this.scopeLabel = data.label || data.Name || '';
            this.page = 1;
            this.loadTableData();
        },

        formatDateTime(v) {
            if (!v) return '-';
            const d = new Date(v);
            if (Number.isNaN(d.getTime())) return String(v).replace('T', ' ').slice(0, 19);
            const pad = n => (n < 10 ? '0' + n : '' + n);
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },

        /** 组装列表/导出查询参数 */
        buildQueryParams(withPaging = true) {
            const range = this.filterForm.dateRange || [];
            const params = {
                schoolId: this.schoolId,
                yearMonth: this.filterForm.yearMonth || undefined,
                campusId: this.filterForm.campusId || undefined,
                classId: this.filterForm.classId || undefined,
                studentName: (this.filterForm.studentName && String(this.filterForm.studentName).trim()) || undefined,
                startDate: range[0] || undefined,
                endDate: range[1] || undefined
            };
            if (withPaging) {
                params.page = this.page;
                params.intPageSize = this.pageSize;
            }
            return params;
        },

        /** 分页加载签到记录 */
        async loadTableData() {
            if (!this.schoolId) return;
            this.loading = true;
            try {
                const res = await getLeagueAffairCheckInRecords(this.buildQueryParams(true));
                if (res.data && res.data.success) {
                    const resp = res.data.response || {};
                    this.tableData = resp.data || [];
                    this.total = resp.dataCount || 0;
                } else {
                    this.tableData = [];
                    this.total = 0;
                    this.$message.error((res.data && res.data.msg) || '加载失败');
                }
            } catch (e) {
                this.tableData = [];
                this.total = 0;
                this.$message.error('加载失败');
            } finally {
                this.loading = false;
            }
        },

        handleSearch() {
            this.page = 1;
            this.loadTableData();
        },

        /** 重置日期/学生等，保留左侧树选中的组织范围 */
        handleReset() {
            this.filterForm.dateRange = [];
            this.filterForm.yearMonth = '';
            this.filterForm.studentName = '';
            this.page = 1;
            this.loadTableData();
        },

        handleSizeChange() {
            this.page = 1;
            this.loadTableData();
        },

        /** 导出签到记录 Excel（blob）。创建于 2026-09-24 */
        async handleExport() {
            if (!this.schoolId) {
                this.$message.warning('请先选择学校');
                return;
            }
            this.exporting = true;
            try {
                const res = await exportLeagueAffairCheckInRecords(this.buildQueryParams(false));
                const blob = res.data;
                if (blob && blob.type && blob.type.indexOf('application/json') >= 0) {
                    const text = await blob.text();
                    let msg = '导出失败';
                    try {
                        const json = JSON.parse(text);
                        msg = json.msg || msg;
                    } catch (err) { /* 非 JSON，可能是纯文本错误 */ }
                    if (!msg || msg === '导出失败') {
                        try { msg = text || msg; } catch (e2) { /* ignore */ }
                    }
                    this.$message.error(msg);
                    return;
                }
                if (!blob || blob.size === 0) {
                    this.$message.warning('没有数据可导出');
                    return;
                }
                const ym = this.filterForm.yearMonth || new Date().toISOString().slice(0, 7);
                const fileName = `团务签到记录_${ym}.xlsx`;
                const url = window.URL.createObjectURL(new Blob([blob], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }));
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                link.click();
                window.URL.revokeObjectURL(url);
                this.$message.success('导出成功');
            } catch (e) {
                this.$message.error('导出失败');
            } finally {
                this.exporting = false;
            }
        }
    }
};
</script>

<style scoped>
.league-affair-checkin-page .filter-bar {
    margin-bottom: 10px;
    flex: 0 0 auto;
}
.league-affair-checkin-page .scope-form-item {
    margin-left: 4px;
}
.league-affair-checkin-page .scope-tag {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}
.league-affair-checkin-page .scope-tag i {
    margin-right: 4px;
}
.league-affair-checkin-page .thumb {
    width: 48px;
    height: 48px;
    border-radius: 4px;
    overflow: hidden;
}
.league-affair-checkin-page .pager {
    margin-top: 12px;
    text-align: right;
    flex: 0 0 auto;
}
.league-affair-checkin-page .muted {
    color: #C0C4CC;
}
.league-affair-checkin-page .compact-table ::v-deep .el-table__body-wrapper {
    overflow-x: hidden !important;
}
.league-affair-checkin-page .compact-table:not(.el-table--scrollable-y) ::v-deep .el-table__body-wrapper {
    overflow-y: hidden !important;
}
.no-department-tip {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
.no-department-tip__inner {
    text-align: center;
    color: #909399;
}
.no-department-tip__inner i {
    font-size: 36px;
    margin-bottom: 8px;
}
</style>
